import { useEffect, useId, useRef, useState } from 'react'
import { X } from 'lucide-react'
import { formatMoney } from '../../lib/format'
import { NOTE_MAX_LENGTH } from '../../lib/requestPolicy'
import { useAppStore } from '../../store/MockAppStore'
import type { MenuItem, SalonRequest } from '../../types'
import { Icon } from '../../components/Icon'
import { TurnstileWidget } from '../../components/TurnstileWidget'

interface Props {
  item: MenuItem
  location: string
  onClose: () => void
  onSuccess: (request: SalonRequest) => void
}

export function RequestComposer({ item, location, onClose, onSuccess }: Props) {
  const { submitRequest } = useAppStore()
  const [notes, setNotes] = useState('')
  const [acknowledged, setAcknowledged] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [turnstileToken, setTurnstileToken] = useState('')
  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined
  const titleId = useId()
  const closeRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    closeRef.current?.focus()
    const onKeyDown = (event: KeyboardEvent) => event.key === 'Escape' && onClose()
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  const handleSubmit = async () => {
    setError('')
    setSubmitting(true)
    try {
      const request = await submitRequest({
        itemId: item.id,
        notes,
        location,
        idempotencyKey: crypto.randomUUID(),
        paidAcknowledged: acknowledged,
        turnstileToken: turnstileToken || undefined,
      })
      onSuccess(request)
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="sheet-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="request-sheet" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className="sheet-grabber" aria-hidden="true" />
        <div className="request-sheet__head">
          <span className={`item-icon item-icon--${item.category}`}><Icon name={item.icon} size={24} /></span>
          <button ref={closeRef} className="icon-button" onClick={onClose} aria-label="Close request form"><X size={20} /></button>
        </div>
        <p className="eyebrow">{item.priceCents > 0 ? 'Paid item · subject to approval' : item.category === 'upgrade' ? 'Optional upgrade' : 'Your request'}</p>
        <h2 id={titleId}>{item.name}</h2>
        <p className="muted">{item.description}</p>

        {item.priceCents > 0 && (
          <div className="price-agreement">
            <div><span>Requested item price</span><strong>{formatMoney(item.priceCents)}</strong></div>
            <label className="check-row">
              <input type="checkbox" checked={acknowledged} onChange={(event) => setAcknowledged(event.target.checked)} />
              <span>I understand this is a request for approval, not a purchase. No payment happens here. If approved, my stylist will add it to my salon ticket.</span>
            </label>
          </div>
        )}

        <label className="field-label" htmlFor="request-note">Anything you’d like us to know? <span>Optional</span></label>
        <textarea
          id="request-note"
          value={notes}
          maxLength={NOTE_MAX_LENGTH}
          onChange={(event) => setNotes(event.target.value)}
          placeholder={item.id === 'something-else' ? 'Tell us what would help' : item.category === 'comfort' ? 'For example: the cape feels a little tight' : 'Add a short note'}
          rows={3}
        />
        <div className="field-meta"><span>{location}</span><span>{notes.length}/{NOTE_MAX_LENGTH}</span></div>
        {turnstileSiteKey && <TurnstileWidget siteKey={turnstileSiteKey} onToken={setTurnstileToken} />}
        {error && <p className="form-error" role="alert">{error}</p>}
        <button
          className="primary-button primary-button--full"
          disabled={submitting || (item.priceCents > 0 && !acknowledged) || Boolean(turnstileSiteKey && !turnstileToken)}
          onClick={handleSubmit}
        >
          {submitting ? <><span className="spinner" /> Sending request…</> : <>Send discreet request <Icon name="chevron" size={18} /></>}
        </button>
        <p className="sheet-note">Your stylist will see this when they’re able. For urgent needs, please speak to them directly.</p>
      </section>
    </div>
  )
}
